import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module icons and colors
const MODULE_CONFIG = {
  module_1: { icon: "💡", name: "Idea Lab", color: "bg-yellow-100 text-yellow-700" },
  module_2: { icon: "🦅", name: "Bird Squad", color: "bg-blue-100 text-blue-700" },
  module_3: { icon: "📋", name: "Blueprint", color: "bg-green-100 text-green-700" },
  module_4: { icon: "🎨", name: "Brand", color: "bg-pink-100 text-pink-700" },
  module_5: { icon: "💰", name: "Money", color: "bg-emerald-100 text-emerald-700" },
  module_6: { icon: "🚀", name: "Launch", color: "bg-orange-100 text-orange-700" },
  module_7: { icon: "⭐", name: "Dream", color: "bg-purple-100 text-purple-700" }
};

export default function CompareClassesPage({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [error, setError] = useState("");

  // Fetch comparison data
  const fetchComparison = async (classIds = null) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ educator_id: user.id });
      if (classIds && classIds.length > 0) {
        params.append("class_ids", classIds.join(","));
      }
      
      const response = await axios.get(`${API}/game/teacher/compare-classes?${params}`);
      setComparison(response.data);
      setAvailableClasses(response.data.available_classes || []);
      
      // Auto-select all classes if none selected
      if (selectedClasses.length === 0 && response.data.classes?.length > 0) {
        setSelectedClasses(response.data.classes.map(c => c.class_id));
      }
    } catch (err) {
      console.error("Failed to fetch comparison:", err);
      setError("Failed to load comparison data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComparison();
  }, [user.id]);

  // Toggle class selection
  const toggleClassSelection = (classId) => {
    const newSelection = selectedClasses.includes(classId)
      ? selectedClasses.filter(id => id !== classId)
      : [...selectedClasses, classId];
    setSelectedClasses(newSelection);
  };

  // Apply selection filter
  const applyFilter = () => {
    fetchComparison(selectedClasses);
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({ educator_id: user.id });
      if (selectedClasses.length > 0) {
        params.append("class_ids", selectedClasses.join(","));
      }
      
      const response = await axios.get(
        `${API}/game/teacher/compare-classes/export/csv?${params}`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "class_comparison.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to export CSV:", err);
      setError("Failed to export comparison");
    }
  };

  // Get color based on percentage (for visual clarity)
  const getPercentageColor = (pct) => {
    if (pct >= 75) return "text-green-600 bg-green-50";
    if (pct >= 50) return "text-yellow-600 bg-yellow-50";
    if (pct >= 25) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  // Find best performing class for a metric
  const getBestClass = (metric) => {
    if (!comparison?.classes?.length) return null;
    return comparison.classes.reduce((best, cls) => 
      cls[metric] > (best?.[metric] || 0) ? cls : best
    , null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8" data-testid="compare-classes-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              📊 Compare Classes
            </h1>
            <p className="text-gray-600 mt-1">
              Side-by-side performance comparison across your classes
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 flex gap-3">
            <Button
              onClick={handleExportCSV}
              disabled={loading || !comparison?.classes?.length}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="export-comparison-csv"
            >
              📥 Export CSV
            </Button>
            <Link to="/game/reports">
              <Button variant="outline" data-testid="back-to-reports">
                ← Back to Reports
              </Button>
            </Link>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="text-4xl animate-bounce mb-4">📊</div>
              <p className="text-gray-600">Loading comparison data...</p>
            </div>
          </div>
        )}

        {/* No Classes */}
        {!loading && (!comparison?.classes || comparison.classes.length === 0) && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">📚</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No Classes to Compare</h2>
            <p className="text-gray-600">Create classes and enroll students to start tracking performance.</p>
          </div>
        )}

        {/* Comparison Content */}
        {!loading && comparison?.classes?.length > 0 && (
          <>
            {/* Class Selector */}
            {availableClasses.length > 1 && (
              <div className="bg-white rounded-xl shadow p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Select classes to compare:</span>
                  <div className="flex flex-wrap gap-2">
                    {availableClasses.map((cls) => (
                      <button
                        key={cls.id}
                        onClick={() => toggleClassSelection(cls.id)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                          selectedClasses.includes(cls.id)
                            ? "bg-indigo-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        data-testid={`select-class-${cls.id}`}
                      >
                        {cls.name}
                      </button>
                    ))}
                  </div>
                  <Button
                    onClick={applyFilter}
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="apply-filter"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl shadow p-5 text-center">
                <div className="text-3xl font-bold text-indigo-600" data-testid="total-classes">
                  {comparison.summary.total_classes}
                </div>
                <div className="text-sm text-gray-600">Classes</div>
              </div>
              <div className="bg-white rounded-xl shadow p-5 text-center">
                <div className="text-3xl font-bold text-blue-600" data-testid="total-students">
                  {comparison.summary.total_students}
                </div>
                <div className="text-sm text-gray-600">Total Students</div>
              </div>
              <div className="bg-white rounded-xl shadow p-5 text-center">
                <div className="text-3xl font-bold text-green-600" data-testid="avg-participation">
                  {comparison.summary.avg_participation_rate}%
                </div>
                <div className="text-sm text-gray-600">Avg Participation</div>
              </div>
              <div className="bg-white rounded-xl shadow p-5 text-center">
                <div className="text-3xl font-bold text-purple-600" data-testid="total-certificates">
                  {comparison.summary.total_certificates}
                </div>
                <div className="text-sm text-gray-600">Certificates Earned</div>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-white rounded-xl shadow overflow-hidden mb-8">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="comparison-table">
                  <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Class</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Students</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Participation</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Games</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Avg Score</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Badges</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Plans</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Certs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {comparison.classes.map((cls, idx) => {
                      const isBestParticipation = getBestClass("participation_rate")?.class_id === cls.class_id;
                      const isBestScore = getBestClass("avg_score")?.class_id === cls.class_id;
                      
                      return (
                        <tr 
                          key={cls.class_id}
                          className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                          data-testid={`class-row-${cls.class_id}`}
                        >
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-900">{cls.class_name}</div>
                            <div className="text-xs text-gray-500">{cls.students_active}/{cls.total_students} active</div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="font-semibold text-gray-900">{cls.total_students}</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getPercentageColor(cls.participation_rate)}`}>
                              {cls.participation_rate}%
                              {isBestParticipation && comparison.classes.length > 1 && " 🏆"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-gray-900">{cls.total_games}</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getPercentageColor(cls.avg_score / 10)}`}>
                              {cls.avg_score}
                              {isBestScore && comparison.classes.length > 1 && " 🏆"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-sm font-medium">
                              🏆 {cls.badges_earned}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-gray-900">{cls.plans_generated}</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-sm font-medium">
                              🎓 {cls.certificates_earned}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Module Completion Comparison */}
            <div className="bg-white rounded-xl shadow p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Module Completion Rates (%)</h3>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="module-completion-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2 text-left text-sm font-medium text-gray-600">Class</th>
                      {Object.entries(MODULE_CONFIG).map(([key, config]) => (
                        <th key={key} className="px-2 py-2 text-center text-xs font-medium text-gray-600">
                          <span className="block">{config.icon}</span>
                          <span>{config.name}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {comparison.classes.map((cls) => (
                      <tr key={cls.class_id}>
                        <td className="px-3 py-3 text-sm font-medium text-gray-900">{cls.class_name}</td>
                        {Object.keys(MODULE_CONFIG).map((moduleId) => {
                          const pct = cls.module_completion[moduleId] || 0;
                          return (
                            <td key={moduleId} className="px-2 py-3 text-center">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getPercentageColor(pct)}`}>
                                {pct}%
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Insights */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl shadow p-6 text-white">
              <h3 className="text-lg font-semibold mb-4">📈 Quick Insights</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white/20 rounded-lg p-4">
                  <div className="text-sm opacity-80">Highest Participation</div>
                  <div className="text-xl font-bold">
                    {getBestClass("participation_rate")?.class_name || "N/A"}
                  </div>
                  <div className="text-sm">
                    {getBestClass("participation_rate")?.participation_rate || 0}% active
                  </div>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <div className="text-sm opacity-80">Top Scorer</div>
                  <div className="text-xl font-bold">
                    {getBestClass("avg_score")?.class_name || "N/A"}
                  </div>
                  <div className="text-sm">
                    {getBestClass("avg_score")?.avg_score || 0} avg points
                  </div>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <div className="text-sm opacity-80">Most Certificates</div>
                  <div className="text-xl font-bold">
                    {getBestClass("certificates_earned")?.class_name || "N/A"}
                  </div>
                  <div className="text-sm">
                    {getBestClass("certificates_earned")?.certificates_earned || 0} earned
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Navigation */}
        <div className="mt-8 text-center">
          <Link 
            to="/game/reports"
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            ← Back to Game Reports
          </Link>
        </div>
      </main>
    </div>
  );
}
